import { useEffect, useMemo, useCallback } from "react"
import { useAEMAllowedToolsContext } from "@context/AEMAllowedToolsContext"
import { useUserStore } from "./useUserStore"
import { useSensitiveTools } from "@AEM/hooks/useSensitiveTools"

export function useAllowedTools() {
	const { tools: AEMAllowedTools } = useAEMAllowedToolsContext()
	const { userProfile } = useUserStore()
	const { data: sensitiveTools = [], isLoading: isSensitiveLoading } =
		useSensitiveTools()

	useEffect(() => {
		if (AEMAllowedTools && AEMAllowedTools.length > 0) {
			sessionStorage.setItem("allowedTools", JSON.stringify(AEMAllowedTools))
		}
	}, [AEMAllowedTools])

	const cachedTools = useMemo(() => {
		try {
			const stored = sessionStorage.getItem("allowedTools")
			return stored ? JSON.parse(stored) : []
		} catch {
			return []
		}
	}, [])

	const effectiveAEMTools =
		AEMAllowedTools && AEMAllowedTools.length > 0
			? AEMAllowedTools
			: cachedTools

	const allowedToolSet = useMemo(() => {
		const allowedTools = new Set<string>()

		const userAllowedSet = new Set(
			userProfile?.allowedApplicationTools?.map((t) => t.toolCode) ?? []
		)

		const sensitiveToolSet = new Set(sensitiveTools)

		for (const toolCode of userAllowedSet) {
			allowedTools.add(toolCode)
		}

		for (const toolCode of effectiveAEMTools) {
			const isSensitive = sensitiveToolSet.has(toolCode)
			const isUserAllowed = userAllowedSet.has(toolCode)

			if (isSensitive) {
				if (isUserAllowed) {
					allowedTools.add(toolCode)
				}
			} else {
				allowedTools.add(toolCode)
			}
		}
		return allowedTools
	}, [sensitiveTools, userProfile?.allowedApplicationTools, effectiveAEMTools])

	const isToolAllowed = useCallback(
		(toolCode: string) => allowedToolSet.has(toolCode),
		[allowedToolSet]
	)

	const isAEMReady = effectiveAEMTools && effectiveAEMTools.length > 0

	const isLoading = !userProfile || isSensitiveLoading || !isAEMReady

	return {
		allowedTools: Array.from(allowedToolSet),
		isToolAllowed,
		isLoading
	}
}
